import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ANOMALY_AUTHORS, ANOMALY_REFRESH_MS } from '@app/features/anomalies/anomaly.model';
import { AnomalyApiError } from '@app/features/anomalies/anomaly-presentation';
import { TourCompletion } from '@app/features/tours/tour-completion';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { HttpErrorInterceptorService } from '@app/shared/errors/http-error-interceptor.service';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  merge,
  Observable,
  Subject,
  Subscription,
  switchMap,
  tap,
  timer,
  map,
} from 'rxjs';
import { announcementErrorMessage } from './announcement-presentation';
import {
  Announcement,
  AnnouncementOutcome,
  AnnouncementRequest,
  VisibleAnnouncement,
} from './announcement.model';
import { canonicalRoles, formerRoleNames } from '@app/features/users/role-names';

export interface IAnnouncementService {
  /** Les annonces à afficher en bandeau à l'utilisateur connecté. */
  visible$: Observable<VisibleAnnouncement[]>;
  /** Toutes les annonces, archivées comprises : la direction seule les reçoit. */
  all$: Observable<Announcement[]>;
  start(): void;
  refresh(): void;
  create(request: AnnouncementRequest): Observable<Announcement>;
  update(id: string, request: AnnouncementRequest): Observable<Announcement>;
  archive(id: string): Observable<Announcement>;
  recordOutcome(id: string, outcome: AnnouncementOutcome): Observable<void>;
}

/** Statuts d'une relecture manquée, qu'on tait : la suivante reprendra dans la minute. */
const SILENT_REFRESH_STATUSES = [0, 500, 502, 503, 504];

/** Refus d'une écriture que l'application dit elle-même en mots (voir `announcementErrorMessage`). */
const WORDED_WRITE_STATUSES = [400, 404, 409];

/**
 * Les annonces de nouveautés (cadrage 2026-032), sur le modèle des anomalies : relues chaque
 * minute, saisies par la direction (RG-nouveaute-annonce). L'API retient ce que l'utilisateur en a
 * fait — fermée, ou parcours suivi jusqu'au bout dans un onglet de démonstration
 * (RG-nouveaute-memoire).
 */
@Injectable({ providedIn: 'root' })
export class AnnouncementService implements IAnnouncementService {
  private readonly http = inject(HttpClient);
  private readonly url = `${inject(RUNTIME_CONFIG).apiUrl}/announcements`;
  private readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);
  private readonly snackBar = inject(MatSnackBar);
  private readonly tourCompletion = inject(TourCompletion);
  private readonly _visibleSubject = new BehaviorSubject<VisibleAnnouncement[]>([]);
  private readonly _allSubject = new BehaviorSubject<Announcement[]>([]);
  private readonly refresh$ = new Subject<void>();
  private polling?: Subscription;

  constructor() {
    const httpErrorInterceptorService = inject(HttpErrorInterceptorService);
    for (const status of SILENT_REFRESH_STATUSES) {
      httpErrorInterceptorService.addIgnoreHttpStatusForUrlBeginningBy(status, 'GET', this.url);
    }
    for (const status of WORDED_WRITE_STATUSES) {
      for (const method of ['POST', 'PUT']) {
        httpErrorInterceptorService.addIgnoreHttpStatusForUrlBeginningBy(status, method, this.url);
      }
    }
    inject(DestroyRef).onDestroy(() => this.polling?.unsubscribe());
  }

  get visible$(): Observable<VisibleAnnouncement[]> {
    return this._visibleSubject.asObservable();
  }

  get all$(): Observable<Announcement[]> {
    return this._allSubject.asObservable();
  }

  /**
   * Lance la relecture des annonces, tout de suite puis chaque minute, et l'écoute des parcours
   * suivis dans un onglet de démonstration.
   */
  start(): void {
    if (this.polling) return;
    this.polling = merge(timer(0, ANOMALY_REFRESH_MS), this.refresh$)
      .pipe(switchMap(() => this.load()))
      .subscribe();
    this.polling.add(
      this.tourCompletion.completed$.subscribe((tourId) => this.recordTourCompleted(tourId)),
    );
  }

  /** Relit sans attendre la minute ; démarre la relecture si elle ne tournait pas encore. */
  refresh(): void {
    if (!this.polling) {
      this.start();
      return;
    }
    this.refresh$.next();
  }

  /** Une relecture qui échoue laisse les bandeaux déjà affichés. */
  private load(): Observable<unknown> {
    const visible = this.http
      .get<VisibleAnnouncement[]>(`${this.url}/visible`)
      .pipe(tap((announcements) => this._visibleSubject.next(announcements)));
    const all = this.authenticatedUserService.hasAnyRole(ANOMALY_AUTHORS)
      ? this.http
          .get<Announcement[]>(this.url)
          .pipe(tap((announcements) => this._allSubject.next(announcements.map(readAnnouncement))))
      : EMPTY;
    return merge(visible.pipe(catchError(() => EMPTY)), all.pipe(catchError(() => EMPTY)));
  }

  create(request: AnnouncementRequest): Observable<Announcement> {
    return this.http.post<Announcement>(this.url, writeAnnouncement(request)).pipe(
      map(readAnnouncement),
      tap(() => this.refresh()),
    );
  }

  update(id: string, request: AnnouncementRequest): Observable<Announcement> {
    return this.http.put<Announcement>(`${this.url}/${id}`, writeAnnouncement(request)).pipe(
      map(readAnnouncement),
      tap(() => this.refresh()),
    );
  }

  archive(id: string): Observable<Announcement> {
    return this.http.post<Announcement>(`${this.url}/${id}/archive`, null).pipe(
      map(readAnnouncement),
      tap(() => this.refresh()),
    );
  }

  /**
   * Fermer une annonce, ou avoir suivi son parcours : le bandeau disparaît aussitôt, l'API le
   * retient. Si l'annonce a changé entre-temps (archivée, plus ciblée), on le dit et on relit.
   */
  recordOutcome(id: string, outcome: AnnouncementOutcome): Observable<void> {
    this._visibleSubject.next(this._visibleSubject.value.filter((a) => a.id !== id));
    return this.http.put<void>(`${this.url}/${id}/outcome`, { outcome }).pipe(
      tap({
        error: (error: AnomalyApiError) => {
          const message = announcementErrorMessage(error, 'outcome');
          if (message) this.snackBar.open(message, 'OK');
          this.refresh();
        },
      }),
      catchError(() => EMPTY),
    );
  }

  /** Le parcours suivi vaut pour chaque annonce visible qui le propose. */
  private recordTourCompleted(tourId: string): void {
    for (const announcement of this._visibleSubject.value) {
      if (announcement.tourId === tourId) {
        this.recordOutcome(announcement.id, 'TOUR_COMPLETED').subscribe();
      }
    }
  }
}

/** Les rôles visés, lus sous leur nouveau nom (cadrage 2026-031, décision 02). */
function readAnnouncement(announcement: Announcement): Announcement {
  return { ...announcement, targetRoles: canonicalRoles(announcement.targetRoles) };
}

/** Les rôles visés, écrits sous leur ancien nom, que l'API accepte avant comme après #338. */
function writeAnnouncement(request: AnnouncementRequest): AnnouncementRequest {
  return { ...request, targetRoles: formerRoleNames(request.targetRoles) };
}
